import { Component, ChangeDetectionStrategy, input, signal, computed } from '@angular/core';

type JsonEntry = readonly [string, unknown];

function isExpandableValue(value: unknown): value is Record<string, unknown> | unknown[] {
    return typeof value === 'object' && value !== null;
}

@Component({
    selector: 'app-json-tree',
    imports: [JsonTreeComponent],
    templateUrl: './json-tree.component.html',
    styleUrls: ['./json-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonTreeComponent {
    readonly value = input<unknown>();
    readonly keyLabel = input<string | null>(null);
    readonly depth = input(0);

    // null = not toggled yet -> fall back to the depth-based default (collapse anything past the first level).
    private readonly collapsedOverride = signal<boolean | null>(null);
    protected readonly collapsed = computed(() => this.collapsedOverride() ?? this.depth() >= 2);

    protected readonly isExpandable = computed(() => isExpandableValue(this.value()));

    protected readonly entries = computed<JsonEntry[]>(() => {
        const value = this.value();
        if (Array.isArray(value)) return value.map((item, index) => [String(index), item] as JsonEntry);
        if (isExpandableValue(value)) return Object.entries(value);
        return [];
    });

    protected readonly summary = computed(() => {
        const value = this.value();
        if (Array.isArray(value)) return `Array(${value.length})`;
        if (isExpandableValue(value)) return `Object(${Object.keys(value).length})`;
        return '';
    });

    protected readonly valueClass = computed(() => {
        const value = this.value();
        if (value === null || value === undefined) return 'json-node__value--null';
        return `json-node__value--${typeof value}`;
    });

    protected readonly formattedValue = computed(() => {
        const value = this.value();
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';
        if (typeof value === 'string') return `"${value}"`;
        return String(value);
    });

    protected toggle(): void {
        this.collapsedOverride.set(!this.collapsed());
    }
}
